/**
 * Label Print Service
 * ────────────────────
 * Renders a LabelTemplate with real data onto a <canvas>, converts
 * the bitmap to ESC/POS raster commands (TSPL / ESC/POS image), and
 * sends the result to a BLE thermal printer.
 */

import {
    LabelTemplate,
    LabelElement,
    LABEL_SIZES,
    DataFieldKey,
    LabelSize,
} from '../store/label-template.store';
import { bluetoothService } from './bluetooth.service';

// @ts-ignore
import GBK from 'gbk.js';

// ─── Print data mapping ─────────────────────────────────────
export interface LabelPrintData {
    tracking_no?: string;
    receiver_name?: string;
    receiver_phone?: string;
    receiver_address?: string;
    sender_name?: string;
    sender_phone?: string;
    sender_address?: string;
    weight?: string;
    volume_weight?: string;
    pieces?: string;
    batch_no?: string;
    remark?: string;
    date?: string;
    custom?: string;
    [key: string]: string | undefined;
}

// ─── DPI / Scale constants ──────────────────────────────────
const PRINT_DPI = 203; // Standard 203 DPI thermal printer
const MM_TO_DOT = PRINT_DPI / 25.4; // 1mm ≈ 8 dots at 203DPI
const CANVAS_SCALE = 2; // Render at 2× for quality

// ─── Render template to Canvas ──────────────────────────────
export async function renderTemplateToCanvas(
    template: LabelTemplate,
    data: LabelPrintData,
): Promise<HTMLCanvasElement> {
    const { w, h } = LABEL_SIZES[template.size];
    const canvasW = Math.round(w * MM_TO_DOT);
    const canvasH = Math.round(h * MM_TO_DOT);

    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d')!;

    // White background
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvasW, canvasH);

    // Render each element
    for (const el of template.elements) {
        await renderElement(ctx, el, data, canvasW, canvasH, w, h);
    }

    return canvas;
}

async function renderElement(
    ctx: CanvasRenderingContext2D,
    el: LabelElement,
    data: LabelPrintData,
    canvasW: number,
    canvasH: number,
    labelW: number,
    labelH: number,
): Promise<void> {
    const scaleX = canvasW / labelW;
    const scaleY = canvasH / labelH;
    const x = el.x * scaleX;
    const y = el.y * scaleY;
    const w = el.width * scaleX;
    const h = el.height * scaleY;

    // Resolve text from data binding
    const resolveText = (): string => {
        if (el.dataField && el.dataField !== 'custom') {
            return data[el.dataField] || '';
        }
        return el.text || '';
    };

    ctx.save();

    switch (el.type) {
        case 'text': {
            const text = resolveText();
            if (!text) break;
            const fontSize = (el.fontSize || 10) * scaleX * 0.9;
            ctx.font = `${el.fontWeight === 'bold' ? 'bold ' : ''}${fontSize}px sans-serif`;
            ctx.fillStyle = '#000';
            ctx.textBaseline = 'top';

            const align = el.textAlign || 'left';
            if (align === 'center') ctx.textAlign = 'center';
            else if (align === 'right') ctx.textAlign = 'right';
            else ctx.textAlign = 'left';

            const textX = align === 'center' ? x + w / 2 : align === 'right' ? x + w : x;

            // Simple word wrap
            const words = text.split('');
            let line = '';
            let lineY = y;
            const lineHeight = fontSize * 1.3;

            for (const char of words) {
                const testLine = line + char;
                const metrics = ctx.measureText(testLine);
                if (metrics.width > w && line.length > 0) {
                    ctx.fillText(line, textX, lineY);
                    line = char;
                    lineY += lineHeight;
                    if (lineY > y + h) break;
                } else {
                    line = testLine;
                }
            }
            if (line && lineY <= y + h) {
                ctx.fillText(line, textX, lineY);
            }
            break;
        }

        case 'barcode': {
            const value = resolveText();
            if (!value) break;

            // Use JsBarcode if available
            try {
                const JsBarcode = (await import('jsbarcode')).default;
                const barcodeCanvas = document.createElement('canvas');
                JsBarcode(barcodeCanvas, value, {
                    format: el.barcodeFormat || 'CODE128',
                    width: 2,
                    height: h * 0.75,
                    displayValue: true,
                    fontSize: Math.max(10, fontSize_calc(el, scaleX)),
                    margin: 2,
                });
                ctx.drawImage(barcodeCanvas, x, y, w, h);
            } catch {
                // Fallback: draw placeholder barcode
                ctx.fillStyle = '#000';
                for (let i = 0; i < 60; i++) {
                    const bw = i % 3 === 0 ? 3 : i % 5 === 0 ? 2 : 1;
                    ctx.fillRect(x + (i * w / 60), y, bw, h * 0.7);
                }
                ctx.font = `${12 * scaleX * 0.5}px monospace`;
                ctx.textAlign = 'center';
                ctx.fillText(value, x + w / 2, y + h * 0.85);
            }
            break;
        }

        case 'qrcode': {
            const value = resolveText();
            if (!value) break;

            try {
                const QRCode = (await import('qrcode')).default;
                const qrDataUrl = await QRCode.toDataURL(value, {
                    width: Math.round(w),
                    margin: 1,
                    errorCorrectionLevel: 'M',
                });
                const img = await loadImage(qrDataUrl);
                ctx.drawImage(img, x, y, w, h);
            } catch {
                // Fallback: black square
                ctx.fillStyle = '#000';
                ctx.fillRect(x, y, w, h);
            }
            break;
        }

        case 'line': {
            ctx.strokeStyle = '#000';
            ctx.lineWidth = (el.lineWidth || 1) * scaleX;
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + w, y);
            ctx.stroke();
            break;
        }

        case 'rect': {
            ctx.strokeStyle = '#000';
            ctx.lineWidth = (el.borderWidth || 1) * scaleX;
            if (el.filled) {
                ctx.fillStyle = '#ddd';
                ctx.fillRect(x, y, w, h);
            }
            ctx.strokeRect(x, y, w, h);
            break;
        }

        case 'image': {
            if (el.imageUrl) {
                try {
                    const img = await loadImage(el.imageUrl);
                    ctx.drawImage(img, x, y, w, h);
                } catch {
                    // ignore
                }
            }
            break;
        }
    }

    ctx.restore();
}

function fontSize_calc(el: LabelElement, scaleX: number): number {
    return (el.fontSize || 10) * scaleX * 0.6;
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

// ─── Canvas → ESC/POS raster image ─────────────────────────
/**
 * Convert canvas to ESC/POS raster image commands.
 * Thermal printers expect 1-bit bitmaps sent line by line.
 *
 * Protocol: ESC/POS "GS v 0" raster bit image
 *   GS v 0 m xL xH yL yH [data]
 *   m = 0 (normal), xL/xH = bytes per line, yL/yH = total lines
 */
export function canvasToEscPosRaster(canvas: HTMLCanvasElement): Uint8Array {
    const ctx = canvas.getContext('2d')!;
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imgData.data;
    const width = canvas.width;
    const height = canvas.height;

    // Calculate ESC/POS bitmap params
    const bytesPerLine = Math.ceil(width / 8);
    const bitmapData: number[] = [];

    // Convert to 1-bit monochrome (threshold)
    for (let row = 0; row < height; row++) {
        for (let byteIdx = 0; byteIdx < bytesPerLine; byteIdx++) {
            let byte = 0;
            for (let bit = 0; bit < 8; bit++) {
                const col = byteIdx * 8 + bit;
                if (col < width) {
                    const idx = (row * width + col) * 4;
                    const r = pixels[idx];
                    const g = pixels[idx + 1];
                    const b = pixels[idx + 2];
                    // Simple threshold: dark pixel = ink
                    const gray = 0.299 * r + 0.587 * g + 0.114 * b;
                    if (gray < 128) {
                        byte |= (0x80 >> bit);
                    }
                }
            }
            bitmapData.push(byte);
        }
    }

    // Build ESC/POS command
    // ESC @ (init) + GS v 0 (raster image)
    const header = new Uint8Array([
        0x1B, 0x40,                          // ESC @ — Initialize
        0x1D, 0x76, 0x30, 0x00,              // GS v 0 m (m=0 normal mode)
        bytesPerLine & 0xFF,                 // xL
        (bytesPerLine >> 8) & 0xFF,          // xH
        height & 0xFF,                       // yL
        (height >> 8) & 0xFF,               // yH
    ]);

    // Label printers: no cut, no extra feed — just flush the print buffer
    const footer = new Uint8Array([
        0x0A,                                // Single LF to flush
    ]);

    const result = new Uint8Array(header.length + bitmapData.length + footer.length);
    result.set(header, 0);
    result.set(new Uint8Array(bitmapData), header.length);
    result.set(footer, header.length + bitmapData.length);

    return result;
}

// ─── TSPL Alternative (for TSPL-protocol printers) ──────────
/**
 * Generate TSPL commands for label printing.
 * Many Chinese BLE label printers use TSPL protocol.
 */
export function canvasToTspl(
    canvas: HTMLCanvasElement,
    labelW: number,
    labelH: number,
    options?: { density?: number; gap?: number; autoDetectGap?: boolean }
): Uint8Array {
    const ctx = canvas.getContext('2d')!;
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imgData.data;
    const width = canvas.width;
    const height = canvas.height;

    const bytesPerLine = Math.ceil(width / 8);
    const bitmapData: number[] = [];

    for (let row = 0; row < height; row++) {
        for (let byteIdx = 0; byteIdx < bytesPerLine; byteIdx++) {
            let byte = 0;
            for (let bit = 0; bit < 8; bit++) {
                const col = byteIdx * 8 + bit;
                if (col < width) {
                    const idx = (row * width + col) * 4;
                    const gray = 0.299 * pixels[idx] + 0.587 * pixels[idx + 1] + 0.114 * pixels[idx + 2];
                    if (gray < 128) {
                        byte |= (0x80 >> bit);
                    }
                }
            }
            bitmapData.push(byte);
        }
    }

    // CRITICAL: Use real CR+LF (\r\n which is 0x0D 0x0A), NOT literal "\\r\\n" text.
    // TSPL printers parse commands line-by-line using CR+LF as delimiter.
    const CRLF = '\r\n';
    const encoder = new TextEncoder();

    // Configurable gap and density
    const gapH = options?.gap ?? 3;
    const densityCmd = options?.density !== undefined ? `DENSITY ${options.density}${CRLF}` : `DENSITY 8${CRLF}`;
    const autoDetectCmd = options?.autoDetectGap ? `GAPDETECT${CRLF}` : '';

    const commands = [
        `SIZE ${labelW} mm, ${labelH} mm${CRLF}`,
        `GAP ${gapH} mm, 0 mm${CRLF}`,
        autoDetectCmd,
        densityCmd,
        `DIRECTION 1${CRLF}`,
        `REFERENCE 0,0${CRLF}`,
        `SET TEAR ON${CRLF}`,
        `CLS${CRLF}`,
        `BITMAP 0,0,${bytesPerLine},${height},0,`,
    ];

    const headerText = encoder.encode(commands.join(''));
    const footerText = encoder.encode(`${CRLF}PRINT 1,1${CRLF}`);

    const result = new Uint8Array(headerText.length + bitmapData.length + footerText.length);
    result.set(headerText, 0);
    result.set(new Uint8Array(bitmapData), headerText.length);
    result.set(footerText, headerText.length + bitmapData.length);

    return result;
}

// ─── CPCL Bitmap (for CPCL printers using raster image) ─────
/**
 * Convert canvas to CPCL bitmap commands.
 * Uses CPCL EG (Expanded Graphics) command to send a raster image.
 * This is FAR more compatible than TEXT commands because it doesn't
 * depend on the printer having specific fonts or being in a specific mode.
 *
 * EG format: EG <widthBytes> <height> <x> <y> <data>
 * Where data is raw monochrome bitmap bytes (1 = black, 0 = white).
 */
export function canvasToCpcl(canvas: HTMLCanvasElement, labelW: number, labelH: number): Uint8Array {
    const ctx = canvas.getContext('2d')!;
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imgData.data;
    const width = canvas.width;
    const height = canvas.height;

    const bytesPerLine = Math.ceil(width / 8);
    const bitmapData: number[] = [];

    // Convert to 1-bit monochrome
    for (let row = 0; row < height; row++) {
        for (let byteIdx = 0; byteIdx < bytesPerLine; byteIdx++) {
            let byte = 0;
            for (let bit = 0; bit < 8; bit++) {
                const col = byteIdx * 8 + bit;
                if (col < width) {
                    const idx = (row * width + col) * 4;
                    const gray = 0.299 * pixels[idx] + 0.587 * pixels[idx + 1] + 0.114 * pixels[idx + 2];
                    if (gray < 128) {
                        byte |= (0x80 >> bit);
                    }
                }
            }
            bitmapData.push(byte);
        }
    }

    const CRLF = '\r\n';
    // CPCL header: ! offset xdpi ydpi height qty
    const pageH = Math.max(8, height);
    const commands = [
        `! 0 200 200 ${pageH} 1`,
        `PW ${width}`,
        `TONE 0`,
        `SPEED 3`,
        // EG = Expanded Graphics: sends raw uncompressed bitmap
        `EG ${bytesPerLine} ${height} 0 0`,
    ];

    const headerStr = commands.join(CRLF) + CRLF;
    const encoder = new TextEncoder();
    const headerBytes = encoder.encode(headerStr);

    // Footer: FORM (find gap) + PRINT (execute)
    const footerStr = `${CRLF}FORM${CRLF}PRINT${CRLF}`;
    const footerBytes = encoder.encode(footerStr);

    const result = new Uint8Array(headerBytes.length + bitmapData.length + footerBytes.length);
    result.set(headerBytes, 0);
    result.set(new Uint8Array(bitmapData), headerBytes.length);
    result.set(footerBytes, headerBytes.length + bitmapData.length);

    console.log(`[CPCL BITMAP] Generated ${result.length} bytes (${bytesPerLine}x${height} bitmap + FORM + PRINT)`);
    return result;
}

// ─── CPCL Alternative (for HPRT / Zebra / CPCL printers) ─────
/**
 * Convert template data to native CPCL instructions.
 * 
 * IMPORTANT: This function generates lightweight TEXT-based CPCL commands
 * (~500 bytes) instead of large bitmap data (~9KB). The D35BT printer
 * (and similar small BLE thermal printers) cannot process large EG bitmap
 * data — the printer receives it but never feeds paper.
 * 
 * Native TEXT commands are processed by the printer's built-in CPCL engine,
 * which handles font rendering, barcodes, and QR codes internally.
 */
export function templateToNativeCpcl(template: LabelTemplate, data: LabelPrintData): Uint8Array {
    const cpclCommands: string[] = [];
    const { w, h } = LABEL_SIZES[template.size]; // physical width height in mm

    const offsetX = template.offsetX || 0;
    const offsetY = template.offsetY || 0;

    // 203 DPI Printer (standard thermal) -> ~8 dots per mm
    const DOTS_PER_MM = 8;
    const pageW = Math.round(w * DOTS_PER_MM);
    // pageH = label height only (NOT including gap).
    // height MUST NOT be 0, otherwise printer will freeze.
    const pageH = Math.max(8, Math.round(h * DOTS_PER_MM));

    // Header: ! offset x-dpi y-dpi height qty
    cpclCommands.push(`! 0 200 200 ${pageH} 1`);
    // PW handles the print width explicitly
    cpclCommands.push(`PW ${pageW}`);
    // Explicitly set media sensing to avoid excessive paper feed
    cpclCommands.push(`GAP-SENSE`);
    // Tuning commands for most mobile label printers
    cpclCommands.push(`SPEED 3`);
    cpclCommands.push(`DENSITY 8`);
    cpclCommands.push(`COUNTRY CHINA`);

    console.log(`[CPCL NATIVE] Template: "${template.name}", Size: ${w}x${h}mm (${pageW}x${pageH} dots), Elements: ${template.elements.length}`);

    for (const el of template.elements) {
        const x = Math.round((el.x + offsetX) * DOTS_PER_MM);
        const y = Math.round((el.y + offsetY) * DOTS_PER_MM);
        const width = Math.round(el.width * DOTS_PER_MM);
        const height = Math.round(el.height * DOTS_PER_MM);

        const resolveText = (): string => {
            if (el.dataField && el.dataField !== 'custom') {
                return data[el.dataField] || '';
            }
            return el.text || '';
        };

        switch (el.type) {
            case 'rect':
                const thick = Math.round((el.borderWidth || 1) * 2);
                cpclCommands.push(`BOX ${x} ${y} ${x + width} ${y + height} ${thick}`);
                break;

            case 'line':
                const lineThick = Math.round((el.lineWidth || 1) * 2) || 2;
                cpclCommands.push(`LINE ${x} ${y} ${x + width} ${y + height} ${lineThick}`);
                break;

            case 'barcode': {
                const val = resolveText();
                if (val) {
                    // BARCODE type width ratio height x y data
                    // 128 format, width=2, ratio=1
                    const barcodeHeight = Math.max(30, Math.round(height * 0.8));
                    cpclCommands.push(`BARCODE 128 2 1 ${barcodeHeight} ${x} ${y} ${val}`);
                    // Human readable text underneath
                    cpclCommands.push(`TEXT 1 0 ${x} ${y + barcodeHeight + 5} ${val}`);
                    console.log(`[CPCL NATIVE] Barcode: "${val}" at (${x},${y}) h=${barcodeHeight}`);
                }
                break;
            }

            case 'qrcode': {
                const val = resolveText();
                if (val) {
                    // BARCODE QR x y M model U size 
                    // U size controls module width - calculate from element width
                    const uSize = Math.max(3, Math.min(10, Math.round(width / 25)));
                    cpclCommands.push(`BARCODE QR ${x} ${y} M 2 U ${uSize}`);
                    cpclCommands.push(`MA,${val}`);
                    cpclCommands.push(`ENDQR`);
                    console.log(`[CPCL NATIVE] QR: "${val}" at (${x},${y}) U=${uSize}`);
                }
                break;
            }

            case 'text': {
                let val = resolveText();
                if (!val) break;

                // Support Bold and Scaling (Magnification)
                const isBold = el.fontWeight === 'bold';
                const fSize = el.fontSize || 10;

                // Magnification calculation: 10pt is roughly 1x, 20pt is 2x
                const mag = Math.max(1, Math.min(3, Math.round(fSize / 10)));

                if (isBold) cpclCommands.push(`SETBOLD 1`);
                if (mag > 1) cpclCommands.push(`SETMAG ${mag} ${mag}`);

                // HPRT Font Mapping: 
                // Font 7 is 24x24 (Chinese + English, best compatibility on D35BT)
                // Font 8 is 24x24 (alternative). Font 4 is 32x32. Font 1 is English only 12x24.
                let font = '7';

                // Character width in dots (for word wrap calculation)
                // Font 7: ~24 dots per CJK char, ~12 dots per ASCII char (before magnification)
                const cjkCharWidth = 24 * mag;
                const asciiCharWidth = 12 * mag;
                const lineHeight = 30 * mag;
                const maxLineWidth = width;

                // Word wrap: split val into lines that fit within element width
                const inputLines = val.split('\n');
                let currY = y;

                for (const inputLine of inputLines) {
                    if (!inputLine.trim()) {
                        currY += lineHeight;
                        if (currY > y + height) break;
                        continue;
                    }

                    // Auto word-wrap for long text lines
                    let currentLine = '';
                    let currentWidth = 0;

                    for (let c = 0; c < inputLine.length; c++) {
                        const char = inputLine[c];
                        const charWidth = char.charCodeAt(0) > 255 ? cjkCharWidth : asciiCharWidth;

                        if (currentWidth + charWidth > maxLineWidth && currentLine.length > 0) {
                            // Emit current line
                            let printX = x;
                            if (el.textAlign === 'center') {
                                printX = x + Math.max(0, (width - currentWidth) / 2);
                            } else if (el.textAlign === 'right') {
                                printX = x + Math.max(0, width - currentWidth);
                            }
                            cpclCommands.push(`TEXT ${font} 0 ${Math.round(printX)} ${currY} ${currentLine}`);
                            currY += lineHeight;
                            if (currY > y + height) break;
                            currentLine = char;
                            currentWidth = charWidth;
                        } else {
                            currentLine += char;
                            currentWidth += charWidth;
                        }
                    }

                    // Emit remaining text
                    if (currentLine && currY <= y + height) {
                        let printX = x;
                        if (el.textAlign === 'center') {
                            printX = x + Math.max(0, (width - currentWidth) / 2);
                        } else if (el.textAlign === 'right') {
                            printX = x + Math.max(0, width - currentWidth);
                        }
                        cpclCommands.push(`TEXT ${font} 0 ${Math.round(printX)} ${currY} ${currentLine}`);
                        currY += lineHeight;
                    }

                    if (currY > y + height) break;
                }

                // Reset state
                if (mag > 1) cpclCommands.push(`SETMAG 1 1`);
                if (isBold) cpclCommands.push(`SETBOLD 0`);

                console.log(`[CPCL NATIVE] Text: "${val.slice(0, 30)}${val.length > 30 ? '...' : ''}" at (${x},${y}), font=${font}, mag=${mag}`);
                break;
            }
        }
    }

    // FORM: use gap sensor to advance to next label boundary.
    // This prevents accumulated drift — the sensor physically detects the gap each time.
    // PRINT: trigger the print job.
    cpclCommands.push(`FORM`);
    cpclCommands.push(`PRINT`);

    // Convert complete command sequence to GBK byte array 
    const finalCmdStr = cpclCommands.join('\r\n') + '\r\n';
    const gbkEncoded = GBK.encode(finalCmdStr);
    const result = new Uint8Array(gbkEncoded);

    console.log(`[CPCL NATIVE ENGINE] Generated ${result.length} bytes (${cpclCommands.length} commands) in GBK format.`);
    console.log(`[CPCL NATIVE ENGINE] Commands preview:\n${finalCmdStr.slice(0, 500)}${finalCmdStr.length > 500 ? '...' : ''}`);
    return result;
}

// ─── High-level print function ──────────────────────────────
export type PrintProtocol = 'escpos' | 'tspl' | 'cpcl' | 'auto';

export interface PrintOptions {
    protocol?: PrintProtocol;
    copies?: number;
    density?: number; // 0-15
    gap?: number;     // e.g. 3 mm
    autoDetectGap?: boolean;
}

/**
 * Detect the likely protocol based on printer name or connection info. 
 * Most Chinese label printers prefix their BLE names with model types.
 */
export function detectPrinterProtocol(deviceName: string): PrintProtocol {
    const name = deviceName.toLowerCase();

    // CPCL: D35BT, Zebra, HM-A series, HPRT CPCL-firmware models
    // D35BT requires CPCL native TEXT commands — bitmap EG doesn't work
    if (
        name.includes('d35') ||
        name.includes('d31') ||
        name.includes('d45') ||
        name.includes('zebra') ||
        name.includes('hm-a') ||
        name.includes('hm-t') ||
        name.includes('qr-') ||
        name.includes('peri')
    ) {
        return 'cpcl';
    }

    // TSPL printers (most common default firmware for Chinese BLE label printers)
    // Based on PPD d520bt-linux.md, D520BT uses rastertolabeltspl filter, so it operates via TSPL.
    if (
        name.includes('d52') ||
        name.includes('xp-') ||
        name.includes('xprinter') ||
        name.includes('tsc') ||
        name.includes('gp-') ||
        name.includes('nb-') ||
        name.includes('gprinter')
    ) {
        return 'tspl';
    }

    // Default to CPCL native — TEXT commands are the safest for unknown printers.
    // CPCL TEXT is lightweight (< 1KB) vs bitmap TSPL BITMAP (> 9KB)
    // which many small BLE printers cannot handle.
    console.log(`[Protocol Detect] Unknown device "${deviceName}", defaulting to CPCL`);
    return 'cpcl';
}

/**
 * Print a label template with data via Bluetooth.
 * This is the main entry point for label printing.
 */
export async function printLabel(
    template: LabelTemplate,
    data: LabelPrintData,
    options: PrintOptions = {},
): Promise<void> {
    const { protocol = 'auto', copies = 1 } = options;

    if (!bluetoothService.isPrinterConnected()) {
        throw new Error('蓝牙打印机未连接，请先在设置中连接打印机');
    }

    // 1. Resolve Protocol
    let activeProtocol = protocol;
    if (protocol === 'auto') {
        const deviceInfo = bluetoothService.getPrinterDevice();
        activeProtocol = detectPrinterProtocol(deviceInfo?.name || '');
        console.log(`[LABEL PRINT] Auto-detected protocol: ${activeProtocol} for device: ${deviceInfo?.name}`);
    }

    // 2. Convert to printer commands
    const { w, h } = LABEL_SIZES[template.size];
    let printData: Uint8Array;

    if (activeProtocol === 'cpcl') {
        // ─── CPCL: Use native TEXT-based commands (NOT bitmap/EG) ───
        // Many small BLE thermal printers (e.g. D35BT) cannot process large
        // EG bitmap raster data (>9KB). Native CPCL TEXT commands are:
        //   - Much smaller (~500 bytes vs ~9KB)
        //   - Processed natively by the printer's CPCL engine
        //   - Proven to work reliably on D35BT and similar printers
        printData = templateToNativeCpcl(template, data);
        console.log(`[LABEL PRINT] Using CPCL native TEXT commands (${printData.length} bytes)`);
    } else if (activeProtocol === 'tspl') {
        // TSPL: Render to canvas, then convert to TSPL BITMAP
        const canvas = await renderTemplateToCanvas(template, data);
        printData = canvasToTspl(canvas, w, h, {
            density: options.density,
            gap: options.gap,
            autoDetectGap: options.autoDetectGap
        });
        console.log(`[LABEL PRINT] Using TSPL BITMAP (${printData.length} bytes)`);
    } else {
        // ESC/POS: Render to canvas, then convert to ESC/POS raster
        const canvas = await renderTemplateToCanvas(template, data);
        printData = canvasToEscPosRaster(canvas);
        console.log(`[LABEL PRINT] Using ESC/POS raster (${printData.length} bytes)`);
    }

    // 3. Send to printer (with copies support)
    for (let i = 0; i < copies; i++) {
        await bluetoothService.printRaw(printData);
        if (i < copies - 1) {
            // Small delay between copies to prevent buffer overflow
            await new Promise(r => setTimeout(r, 600));
        }
    }
}

/**
 * Perform a test print with a simple label.
 */
/**
 * Perform a test print with a simple label.
 * For CPCL: uses native TEXT commands (proven to work on D35BT).
 * For TSPL/ESCPOS: uses canvas-based bitmap rendering.
 */
export async function printTest(protocol: PrintProtocol, size: LabelSize = '76x130'): Promise<void> {
    const { w, h } = LABEL_SIZES[size];

    // Resolve Protocol
    let activeProtocol = protocol;
    if (protocol === 'auto') {
        const deviceInfo = bluetoothService.getPrinterDevice();
        activeProtocol = detectPrinterProtocol(deviceInfo?.name || '');
    }

    console.log(`[TEST PRINT] Protocol: ${activeProtocol}, Size: ${w}x${h}mm`);

    let printData: Uint8Array;

    if (activeProtocol === 'cpcl') {
        // ─── CPCL: Native TEXT commands ──────────────────────────
        // This approach is proven to work on D35BT and similar printers.
        // Generates lightweight ~200 byte commands vs ~9KB bitmap data.
        const CRLF = '\r\n';
        const DOTS_PER_MM = 8;
        const pageW = Math.round(w * DOTS_PER_MM);
        const pageH = Math.max(8, Math.round(h * DOTS_PER_MM));
        const now = new Date().toLocaleTimeString();

        const commands = [
            `! 0 200 200 ${pageH} 1`,
            `PW ${pageW}`,
            `GAP-SENSE`,
            `SPEED 3`,
            `DENSITY 8`,
            `COUNTRY CHINA`,
            // Test content
            `SETBOLD 1`,
            `TEXT 7 0 20 20 HEDUI TEST`,
            `SETBOLD 0`,
            `TEXT 7 0 20 60 CPCL ${w}x${h}mm`,
            `TEXT 7 0 20 100 ${now}`,
            // Barcode test
            `BARCODE 128 2 1 40 20 ${Math.round(pageH * 0.55)} 123456`,
            // Border box
            `BOX 5 5 ${pageW - 5} ${pageH - 5} 2`,
            // Print
            `FORM`,
            `PRINT`,
        ];

        // @ts-ignore
        const GBK = (await import('gbk.js')).default;
        const cmdStr = commands.join(CRLF) + CRLF;
        const gbkEncoded = GBK.encode(cmdStr);
        printData = new Uint8Array(gbkEncoded);

        console.log(`[TEST PRINT] CPCL native TEXT: ${printData.length} bytes`);
        console.log(`[TEST PRINT] Commands:\n${cmdStr}`);
    } else {
        // ─── TSPL / ESC/POS: Canvas bitmap approach ─────────────
        const DOTS_PER_MM = 8;
        const canvasW = Math.round(w * DOTS_PER_MM);
        const canvasH = Math.round(h * DOTS_PER_MM);

        const canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;
        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.strokeStyle = 'black';
        ctx.lineWidth = 4;
        ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);

        ctx.beginPath();
        ctx.moveTo(0, canvas.height / 2);
        ctx.lineTo(canvas.width, canvas.height / 2);
        ctx.moveTo(canvas.width / 2, 0);
        ctx.lineTo(canvas.width / 2, canvas.height);
        ctx.stroke();

        ctx.fillStyle = 'black';
        ctx.font = 'bold 36px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('HEDUI TEST', canvas.width / 2, canvas.height / 2 - 20);
        ctx.font = '24px sans-serif';
        ctx.fillText(`${activeProtocol.toUpperCase()} (${w}x${h}mm)`, canvas.width / 2, canvas.height / 2 + 20);
        ctx.fillText(new Date().toLocaleTimeString(), canvas.width / 2, canvas.height / 2 + 50);

        if (activeProtocol === 'tspl') {
            printData = canvasToTspl(canvas, w, h);
        } else {
            printData = canvasToEscPosRaster(canvas);
        }

        console.log(`[TEST PRINT] ${activeProtocol.toUpperCase()} bitmap: ${printData.length} bytes`);
    }

    await bluetoothService.printRaw(printData);
}


/**
 * Build LabelPrintData from shipment-like object.
 * Utility to normalize data from different page contexts.
 */
export function buildPrintData(params: {
    tracking_no?: string;
    weight?: number;
    length?: number;
    width?: number;
    height?: number;
    batch_no?: string;
    shipper_name?: string;
    receiver_name?: string;
    receiver_phone?: string;
    receiver_address?: string;
    sender_name?: string;
    sender_phone?: string;
    sender_address?: string;
    pieces?: number;
    remark?: string;
}): LabelPrintData {
    const volWeight = ((params.length || 0) * (params.width || 0) * (params.height || 0)) / 6000;

    return {
        tracking_no: params.tracking_no || '',
        receiver_name: params.receiver_name || '',
        receiver_phone: params.receiver_phone || '',
        receiver_address: params.receiver_address || '',
        sender_name: params.sender_name || params.shipper_name || '',
        sender_phone: params.sender_phone || '',
        sender_address: params.sender_address || '',
        weight: params.weight ? `${params.weight.toFixed(2)} kg` : '',
        volume_weight: volWeight > 0 ? `${volWeight.toFixed(2)} kg` : '',
        pieces: params.pieces ? String(params.pieces) : '1',
        batch_no: params.batch_no || '',
        remark: params.remark || '',
        date: new Date().toLocaleDateString('zh-CN'),
    };
}
